import { useLocale } from "../hooks/useLocale";
import { msg } from "../locales/translate";
import { CircleAlert, Clock3, FileSearch, ListChecks, Sparkles } from "lucide-react";
import styled from "styled-components";
import type { FailureAnalysisReport, requestFailureAnalysis } from "../services/transactionFailureAnalysis";

type SectionKind = "outcome" | "evidence" | "steps" | "other";
type Section = { kind: SectionKind; title: string; lines: string[] };

const headings: Record<string, SectionKind> = {
	problem: "outcome", "проблема": "outcome", solution: "steps", "решение": "steps",
	outcome: "outcome", "итог": "outcome", "результат": "outcome",
	"evidence and likely cause": "evidence", "доказательства и вероятная причина": "evidence",
	"факты и вероятная причина": "evidence", "причины и доказательства": "evidence",
	"next steps": "steps", "следующие шаги": "steps", "дальнейшие действия": "steps",
};

function parseSections(text: string): Section[] {
	const sections: Section[] = [];
	let current: Section = { kind: "other", title: "", lines: [] };
	for (const line of text.replace(/\r\n?/g, "\n").split("\n")) {
		const title = line.trim().replace(/^#{1,6}\s+/, "").replace(/^\*\*(.*?)\*\*:?$/, "$1").replace(/:$/, "");
		const kind = headings[title.toLowerCase()];
		if (kind) {
			if (current.title || current.lines.some(line => line.trim())) sections.push(current);
			current = { kind, title, lines: [] };
		} else current.lines.push(line);
	}
	if (current.title || current.lines.some(line => line.trim())) sections.push(current);
	return sections;
}

const Result = styled.section`
	display: flex; flex-direction: column; gap: 12px; min-width: 0;
	color: #344054; font-size: 13px; line-height: 1.75;
`;
const Header = styled.header`
	display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
	padding: 6px 0 2px;
	h3 { display: flex; align-items: center; gap: 8px; margin: 0; color: #182230; font-size: 16px; font-weight: 600; }
	span { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; color: #98a2b3; }
`;
const SectionCard = styled.section<{ $kind: SectionKind }>`
	background: ${({ $kind }) => $kind === "outcome" ? "#fffaf5" : "#fff"};
	border: 1px solid ${({ $kind }) => $kind === "outcome" ? "#fed7aa" : "#d0d5dd"};
	border-radius: 12px; padding: 18px 20px; box-shadow: 0 1px 2px rgba(16,24,40,.05);
	h4 { display: flex; align-items: center; gap: 9px; margin: 0 0 12px;
		font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em;
		color: ${({ $kind }) => $kind === "outcome" ? "#b54708" : $kind === "steps" ? "#027a48" : "#667085"}; }
	p { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; }
	p + p { margin-top: 12px; }
	h2 { margin: 0 0 10px; color: #182230; font-size: 19px; font-weight: 600; line-height: 1.4; overflow-wrap: anywhere; }
	ol, ul { margin: 0; padding-left: 22px; }
	li { padding-left: 4px; overflow-wrap: anywhere; }
	li + li { margin-top: 16px; }
	li::marker { color: #667085; font-weight: 600; }
`;

const Disclosure = styled.details`
	min-width: 0;
	summary { cursor: pointer; color: #475467; font-weight: 500; overflow-wrap: anywhere; }
	summary:focus-visible { outline: 2px solid #167ca6; outline-offset: 4px; border-radius: 3px; }
	&[open] > summary { margin-bottom: 12px; }
	pre { margin: 0; padding: 12px; border: 1px solid #eaecf0; border-radius: 8px; background: #f9fafb;
		font: 12px/1.6 monospace; white-space: pre-wrap; overflow-wrap: anywhere; }
`;
const Meta = styled.div`
	color: #667085; font-size: 12px; margin-top: 6px; white-space: pre-wrap; overflow-wrap: anywhere;
`;
const Owner = styled.span`
	display: inline-block; max-width: 100%; box-sizing: border-box; margin-bottom: 6px; padding: 2px 8px;
	border: 1px solid #d0d5dd; border-radius: 6px; color: #344054; font-size: 11px; line-height: 1.5; overflow-wrap: anywhere;
`;

function StructuredReport({ report, ru }: { report: FailureAnalysisReport; ru: boolean }) {
	useLocale();
	return <>
		<SectionCard $kind="outcome">
			<h4><CircleAlert size={17} aria-hidden="true" />{ru ? msg("message_6f3c31ef") : msg("summary")}</h4>
			<h2>{report.title}</h2><p>{report.summary}</p>
		</SectionCard>
		{report.actions.length > 0 && <SectionCard $kind="steps">
			<h4><ListChecks size={17} aria-hidden="true" />{ru ? msg("message_44d80995") : msg("nextSteps")}</h4>
			<ol>{report.actions.map((item, index) => <li key={index}>
				{item.owner && <Owner>{item.owner}</Owner>}
				<p>{item.action}</p>
				{item.expectedResult && <Meta><strong>{ru ? msg("message_3528089f") : msg("expectedResult")}</strong>{item.expectedResult}</Meta>}
			</li>)}</ol>
		</SectionCard>}
		{report.evidence.length > 0 && <SectionCard $kind="evidence">
			<h4><FileSearch size={17} aria-hidden="true" />{ru ? msg("message_8fe12b16") : msg("analysisEvidence")}</h4>
			<ul>{report.evidence.map((item, index) => <li key={index}>
				<p>{item.explanation}</p>
				{item.reference && <Disclosure style={{ marginTop: 8 }}><summary>{ru ? msg("message_59a093de") : msg("showLogEvidence")}</summary><pre>{item.reference}</pre></Disclosure>}
			</li>)}</ul>
		</SectionCard>}
		{report.unknowns.length > 0 && <SectionCard $kind="other">
			<h4>{ru ? msg("message_9566a250") : msg("whatRemainsUnknown")}</h4>
			<ul>{report.unknowns.map((item, index) => <li key={index}>
				<p><strong>{item.question}</strong></p>
				{item.neededData && <Meta>{ru ? msg("message_b238689f") : msg("dataNeeded")}{item.neededData}</Meta>}
			</li>)}</ul>
		</SectionCard>}
		{report.technicalDetails.trim() && <SectionCard $kind="other">
			<Disclosure><summary>{ru ? msg("message_85dcf639") : msg("technicalDetails")}</summary><p>{report.technicalDetails}</p></Disclosure>
		</SectionCard>}
		{report.secondaryFindings.length > 0 && <SectionCard $kind="other">
			<Disclosure><summary>{ru ? msg("message_47887bc9") : msg("additionalFindings")} ({report.secondaryFindings.length})</summary>
				<ul>{report.secondaryFindings.map((item, index) => <li key={index}>
					<p>{item.finding}</p>
					<Meta>{item.causalLinkProven
						? (ru ? msg("message_2fd974c8") : msg("linkToTheFailureConfirmed"))
						: (ru ? msg("message_7fe2bc8b") : msg("linkToTheFailureNotProven"))}</Meta>
				</li>)}</ul>
			</Disclosure>
		</SectionCard>}
	</>;
}

export function TransactionFailureAnalysisResult({ result }: { result: Awaited<ReturnType<typeof requestFailureAnalysis>> }) {
	useLocale();
	const { responseTime } = result;
	const ru = result.language === "ru";
	return <Result aria-label={ru ? msg("message_d83bad16") : msg("analysisResult")}>
		<Header>
			<h3><Sparkles size={17} color="#667085" />{ru ? msg("message_d83bad16") : msg("analysisResult")}</h3>
			{responseTime !== undefined && <span><Clock3 size={13} />{(responseTime / 1000).toFixed(1)}{msg("s")}</span>}
		</Header>
		{result.report ? <StructuredReport report={result.report} ru={ru} /> : parseSections(result.text).map((section, index) => {
			const Icon = section.kind === "outcome" ? CircleAlert : section.kind === "steps" ? ListChecks : FileSearch;
			return <SectionCard key={index} $kind={section.kind}>
				{section.title && <h4><Icon size={17} aria-hidden="true" />{section.title}</h4>}
				{section.lines.join("\n").trim().split(/\n\s*\n/).filter(Boolean).map((paragraph, index) => <p key={index}>{paragraph}</p>)}
			</SectionCard>;
		})}
	</Result>;
}
